import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../utils/api';
import { isParty } from '../../utils/expenseUtils';
import { fetchGroupEvents, fetchPendingPayments } from '../../utils/eventApi';
import GroupEventsTab from './GroupEventsTab';
import PendingPaymentsPanel from './PendingPaymentsPanel';
import CreateEventModal from './CreateEventModal';
import EventItemModal from './EventItemModal';
import RecordPaymentModal from './RecordPaymentModal';

function GroupDetailView({
  group,
  balances,
  users,
  expenses,
  currentUser,
  onUpdateGroup,
  onDeleteGroup,
  onAddExpenseClick,
  onSettleUpClick,
  onSettleUp,
  onDeleteExpense,
  onEditExpense,
  onDataChanged,
  isMenuOpen,
  onToggleMenu,
  isPinned,
}) {
  const [activeSubTab, setActiveSubTab] = useState('dashboard'); // 'dashboard', 'expenses', 'events', 'members', 'summary'
  const [selectedMemberId, setSelectedMemberId] = useState(group.members?.[0]?.userId || '');

  // Sync selectedMemberId if group members change or if selected member is removed
  useEffect(() => {
    if (group.members && group.members.length > 0) {
      if (!group.members.some((m) => m.userId === selectedMemberId)) {
        setSelectedMemberId(group.members[0].userId);
      }
    } else {
      setSelectedMemberId('');
    }
  }, [group, selectedMemberId]);

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: group.name,
    description: group.description || '',
    type: group.type,
  });
  const [newMemberId, setNewMemberId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Keep editForm synced with group updates
  useEffect(() => {
    setEditForm({
      name: group.name,
      description: group.description || '',
      type: group.type,
    });
  }, [group]);

  const [events, setEvents] = useState([]);
  const [pendingPayments, setPendingPayments] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [eventModal, setEventModal] = useState(null); // { type: 'create' | 'edit' | 'item' | 'payment', eventId, item, itemId, changeTotal }

  const loadEvents = async () => {
    try {
      const [eventsData, pendingData] = await Promise.all([
        fetchGroupEvents(group.id),
        fetchPendingPayments(group.id),
      ]);
      setEvents(eventsData);
      setPendingPayments(pendingData);
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  useEffect(() => {
    setEvents([]);
    setPendingPayments(null);
    setSelectedEventId(null);
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.id]);

  // Event payments move member balances, so the parent's balances are refreshed too.
  const handleEventsChanged = () => {
    loadEvents();
    if (onDataChanged) onDataChanged();
  };

  const modalEvent = eventModal?.eventId ? events.find((e) => e.id === eventModal.eventId) : null;

  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    setError('');
    if (!editForm.name.trim()) {
      setError('Group name is required.');
      return;
    }

    setLoading(true);
    try {
      const res = await apiFetch(`/groups/${group.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: editForm.name.trim(),
          description: editForm.description.trim(),
          type: editForm.type,
        }),
      });

      if (!res.ok) {
        throw new Error('Failed to update group');
      }

      const updated = await res.json();
      onUpdateGroup(updated);
      setIsEditing(false);
    } catch (err) {
      setError(err.message || 'Error updating group details.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (window.confirm(`Are you sure you want to delete "${group.name}"? This action cannot be undone.`)) {
      setLoading(true);
      try {
        const res = await apiFetch(`/groups/${group.id}`, { method: 'DELETE' });
        if (!res.ok) {
          throw new Error('Failed to delete group');
        }
        onDeleteGroup();
      } catch (err) {
        alert(err.message || 'Error deleting group.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!newMemberId) return;

    setLoading(true);
    try {
      const res = await apiFetch(`/groups/${group.id}/members?userId=${newMemberId}`, {
        method: 'POST',
      });

      if (!res.ok) {
        throw new Error('Failed to add member');
      }

      const updated = await res.json();
      onUpdateGroup(updated);
      setNewMemberId('');
    } catch (err) {
      alert(err.message || 'Error adding member.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (userId, userName) => {
    if (window.confirm(`Are you sure you want to remove ${userName} from the group?`)) {
      setLoading(true);
      try {
        const res = await apiFetch(`/groups/${group.id}/members/${userId}`, {
          method: 'DELETE',
        });

        if (!res.ok) {
          throw new Error('Failed to remove member');
        }

        const updated = await res.json();
        onUpdateGroup(updated);
      } catch (err) {
        alert(err.message || 'Error removing member.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleCoAdmin = async (userId, currentRole) => {
    const newRole = currentRole === 'co-admin' ? 'member' : 'co-admin';
    setLoading(true);
    try {
      const res = await apiFetch(`/groups/${group.id}/members/${userId}/role?role=${newRole}`, {
        method: 'PUT',
      });

      if (!res.ok) {
        throw new Error('Failed to update member role');
      }

      const updated = await res.json();
      onUpdateGroup(updated);
    } catch (err) {
      alert(err.message || 'Error updating member role.');
    } finally {
      setLoading(false);
    }
  };

  const getFriendName = (id) => {
    const u = users.find((x) => x.id === id);
    return u ? u.name : 'Unknown';
  };

  const getCategoryEmoji = (desc) => {
    const d = desc.toLowerCase();
    if (d.includes('food') || d.includes('dinner') || d.includes('lunch') || d.includes('eat') || d.includes('drink')) return '🍔';
    if (d.includes('travel') || d.includes('cab') || d.includes('flight') || d.includes('uber') || d.includes('fuel')) return '🚗';
    if (d.includes('rent') || d.includes('flat') || d.includes('stay') || d.includes('hotel')) return '🏠';
    if (d.includes('bill') || d.includes('electricity') || d.includes('wifi') || d.includes('water')) return '💡';
    if (d.includes('settle') || d.includes('payment') || d.includes('paid')) return '🤝';
    return '📦';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Filter users that can be added to this group (non-members)
  const nonMembers = users.filter((u) => !group.members.some((m) => m.userId === u.id));

  // Filter non-zero balances for the dashboard
  const nonZeroBalances = balances.balancesPerUser.filter((b) => Math.abs(b.balance) > 0.01);

  const currentUserMember = group.members?.find((m) => m.userId === currentUser.id);
  const isCreator = group.createdBy === currentUser.id;
  const isCreatorOrAdmin = currentUserMember?.role === 'admin' || isCreator;
  const isCoAdmin = currentUserMember?.role === 'co-admin';
  const canManageGroup = isCreatorOrAdmin || isCoAdmin;
  const canAddOrEditExpenses = isCreatorOrAdmin || isCoAdmin;

  return (
    <div className="group-detail-container">
      {/* Group Header Info */}
      <header className="content-header group-header">
        <button
          className={`hamburger-btn ${isPinned ? 'd-none-desktop' : ''}`}
          onClick={onToggleMenu}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          style={{ background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: '24px', cursor: 'pointer', marginRight: '12px' }}
        >
          {isMenuOpen ? '✕' : '☰'}
        </button>
        {isEditing ? (
          <form onSubmit={handleUpdateGroup} className="group-edit-form">
            {error && <div className="modal-error">{error}</div>}
            <div className="form-row-3">
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Group Name"
                required
                className="edit-name-input"
              />
              <input
                type="text"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Description"
                className="edit-desc-input"
              />
              <select
                value={editForm.type}
                onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                className="edit-type-select"
              >
                <option value="trip">🧳 Trip</option>
                <option value="daily">📅 Daily</option>
              </select>
            </div>
            <div className="edit-form-actions">
              <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                Save
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setIsEditing(false)}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="header-title">
            <div className="group-title-row">
              <h1>{group.name}</h1>
              <span className={`group-type-badge ${group.type}`}>
                {group.type === 'trip' ? '🧳 Trip' : '📅 Daily'}
              </span>
            </div>
            {group.description && <p className="group-desc">{group.description}</p>}
            <p className="group-meta-info">Created by {getFriendName(group.createdBy)}</p>
          </div>
        )}

        {!isEditing && (
          <div className="header-actions">
            {canManageGroup && (
              <button className="btn btn-secondary btn-sm" onClick={() => setIsEditing(true)} title="Edit Group">
                ✏️ Edit
              </button>
            )}
            {isCreator && (
              <button className="btn btn-danger btn-sm" onClick={handleDeleteGroup} disabled={loading} title="Delete Group">
                🗑️ Delete
              </button>
            )}
            <button className="btn btn-secondary" onClick={onSettleUpClick}>
              🤝 Settle Up
            </button>
            <button className="btn btn-primary" onClick={onAddExpenseClick}>
              ➕ Add Expense
            </button>
          </div>
        )}
      </header>

      {/* Sub Tabs Navigation */}
      <div className="group-tabs">
        <button
          className={`group-tab-item ${activeSubTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('dashboard')}
        >
          📊 Dashboard
        </button>
        <button
          className={`group-tab-item ${activeSubTab === 'expenses' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('expenses')}
        >
          🧾 Expenses Log ({expenses.length})
        </button>
        <button
          className={`group-tab-item ${activeSubTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('events')}
        >
          🎟️ Events ({events.length})
        </button>
        <button
          className={`group-tab-item ${activeSubTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('members')}
        >
          👥 Members ({group.members.length})
        </button>
        <button
          className={`group-tab-item ${activeSubTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('summary')}
        >
          👤 Member Summary
        </button>
      </div>

      {/* Tab Content */}
      <div className="group-tab-content">
        {activeSubTab === 'dashboard' && (
          <div className="dashboard-grid">
            <PendingPaymentsPanel
              pending={pendingPayments}
              currentUser={currentUser}
              getName={getFriendName}
              onRecordPayment={(eventId, itemId) => setEventModal({ type: 'payment', eventId, itemId })}
              onSplitItem={(eventId, itemId) =>
                setEventModal({
                  type: 'item',
                  eventId,
                  item: events.find((e) => e.id === eventId)?.items.find((i) => i.id === itemId),
                })
              }
            />

            {/* Overview Stats */}
            <section className="overview-cards">
              <div className="stat-card">
                <div className="stat-label">Group Net Balance</div>
                <div className={`stat-value ${balances.netBalance >= 0 ? 'positive' : 'negative'}`}>
                  {balances.netBalance >= 0 ? `+₹${balances.netBalance.toFixed(2)}` : `-₹${Math.abs(balances.netBalance).toFixed(2)}`}
                </div>
                <div className="stat-indicator">aggregated status in group</div>
              </div>

              <div className="stat-card">
                <div className="stat-label">You owe in group</div>
                <div className="stat-value negative">₹{balances.totalOwedToOthers.toFixed(2)}</div>
                <div className="stat-indicator">to settle up</div>
              </div>

              <div className="stat-card">
                <div className="stat-label">You are owed in group</div>
                <div className="stat-value positive">₹{balances.totalOwedFromOthers.toFixed(2)}</div>
                <div className="stat-indicator">to receive</div>
              </div>
            </section>

            <div className="dashboard-details">
              {/* Group Members Balances */}
              <section className="dashboard-section card-box">
                <h3>👥 Member Balances</h3>
                {nonZeroBalances.length === 0 ? (
                  <div className="empty-state">
                    <p>Everyone is settled up in this group! 🎉</p>
                  </div>
                ) : (
                  <div className="friend-balance-list">
                    {nonZeroBalances.map((friend) => (
                      <div key={friend.userId} className="friend-balance-row">
                        <div className="friend-avatar-circle">
                          {friend.userName.substring(0, 1).toUpperCase()}
                        </div>
                        <div className="friend-info">
                          <span className="friend-name">{friend.userName}</span>
                          <span className={`friend-status ${friend.balance >= 0 ? 'positive' : 'negative'}`}>
                            {friend.balance >= 0 ? 'owes you' : 'you owe'}
                          </span>
                        </div>
                        <div className={`friend-balance-val ${friend.balance >= 0 ? 'positive' : 'negative'}`}>
                          ₹{Math.abs(friend.balance).toFixed(2)}
                        </div>
                        <button
                          className="btn-tiny"
                          onClick={() => onSettleUp(friend)}
                        >
                          Settle
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Recent Group Expenses */}
              <section className="dashboard-section card-box">
                <h3>⏳ Recent Group Expenses</h3>
                {expenses.length === 0 ? (
                  <div className="empty-state">
                    <p>No expenses added in this group yet.</p>
                  </div>
                ) : (
                  <div className="recent-expense-list">
                    {expenses.slice(0, 5).map((expense) => {
                      const paidByMe = expense.paidByUserId === currentUser.id;
                      const isSettlement = expense.description.toLowerCase().includes('settle');
                      return (
                        <div key={expense.id} className="recent-expense-row">
                          <div className="expense-emoji">{getCategoryEmoji(expense.description)}</div>
                          <div className="expense-details-col">
                            <span className="expense-desc">{expense.description}</span>
                            <span className="expense-meta">
                              Paid by {paidByMe ? 'You' : getFriendName(expense.paidByUserId)}
                            </span>
                          </div>
                          <div className="expense-amount-col">
                            <span className="expense-val">₹{expense.amount.toFixed(2)}</span>
                            <span className={`expense-relationship ${paidByMe ? 'positive' : 'negative'}`}>
                              {isSettlement 
                                ? 'settled'
                                : paidByMe 
                                  ? 'you lent' 
                                  : 'you borrowed'
                              }
                            </span>
                          </div>
                          {canAddOrEditExpenses && (
                            <button className="btn-icon-delete" onClick={() => onEditExpense(expense)} title="Edit Expense" style={{ marginRight: '6px' }}>
                              ✏️
                            </button>
                          )}
                          {isParty(expense, currentUser.id) ? (
                            <button className="btn-icon-delete" onClick={() => onDeleteExpense(expense.id)}>
                              🗑️
                            </button>
                          ) : (
                            <span className="btn-icon-delete" title="You can only delete expenses you paid or participated in." style={{cursor: 'help'}}>
                              🔒
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
          </div>
        )}

        {activeSubTab === 'expenses' && (
          <div className="card-box container-large">
            <div className="container-header">
              <h3>🧾 Group Expense Log</h3>
              <span className="count-badge">{expenses.length} total</span>
            </div>

            {expenses.length === 0 ? (
              <div className="empty-state">
                <p>No expenses logged in this group.</p>
              </div>
            ) : (
              <div className="expenses-table">
                <div className="table-header-row">
                  <span>Details</span>
                  <span>Paid By</span>
                  <span>Total Amount</span>
                  <span>Participants Split</span>
                  <span>Actions</span>
                </div>
                <div className="table-body">
                  {expenses.map((expense) => (
                    <div key={expense.id} className="table-row">
                      <div className="table-cell-desc" data-label="Details">
                        <span className="cell-emoji">{getCategoryEmoji(expense.description)}</span>
                        <div className="cell-text">
                          <span className="main-text">{expense.description}</span>
                          <span className="sub-text">{formatDate(expense.createdAt)}</span>
                        </div>
                      </div>
                      <div className="table-cell" data-label="Paid By">
                        <span className="payer-badge">{getFriendName(expense.paidByUserId)}</span>
                      </div>
                      <div className="table-cell val-bold" data-label="Total Amount">₹{expense.amount.toFixed(2)}</div>
                      <div className="table-cell split-details" data-label="Participants Split">
                        {expense.participants?.map((p) => (
                          <div key={p.userId} className="split-detail-pill">
                            <span>{getFriendName(p.userId)}</span>
                            <span>₹{p.amountOwed.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="table-cell text-right" data-label="Actions">
                        {canAddOrEditExpenses && (
                          <button className="btn btn-secondary btn-sm" style={{ marginRight: '8px' }} onClick={() => onEditExpense(expense)}>
                            Edit
                          </button>
                        )}
                        {isParty(expense, currentUser.id) ? (
                          <button className="btn btn-danger btn-sm" onClick={() => onDeleteExpense(expense.id)}>
                            Delete
                          </button>
                        ) : (
                          <span title="You can only delete expenses you paid or participated in.">🔒</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeSubTab === 'events' && (
          <GroupEventsTab
            events={events}
            selectedEventId={selectedEventId}
            onSelectEvent={setSelectedEventId}
            currentUser={currentUser}
            getName={getFriendName}
            canManageGroup={canManageGroup}
            onOpenModal={setEventModal}
            onChanged={handleEventsChanged}
          />
        )}

        {activeSubTab === 'members' && (
          <div className="group-members-container card-box">
            <div className="container-header">
              <h3>👥 Group Members</h3>
              <span className="count-badge">{group.members.length} members</span>
            </div>

            {/* Add Member Section */}
            {canManageGroup && nonMembers.length > 0 && (
              <form onSubmit={handleAddMember} className="add-member-form">
                <select
                  value={newMemberId}
                  onChange={(e) => setNewMemberId(e.target.value)}
                  required
                >
                  <option value="">-- Select Friend to Add --</option>
                  {nonMembers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </option>
                  ))}
                </select>
                <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                  ➕ Add Member
                </button>
              </form>
            )}

            {/* Members List */}
            <div className="group-members-list">
              {group.members.map((member) => {
                const isMe = member.userId === currentUser.id;
                const memberIsAdmin = member.role === 'admin' || group.createdBy === member.userId;
                const memberIsCoAdmin = member.role === 'co-admin';
                return (
                  <div key={member.userId} className="group-member-row">
                    <div className="member-info-col">
                      <div className="member-avatar">
                        {member.userName.substring(0, 2).toUpperCase()}
                      </div>
                      <div className="member-details">
                        <span className="member-name">
                          {member.userName} {isMe && <span className="me-badge">You</span>}
                        </span>
                        <span className="member-email">{member.userEmail}</span>
                      </div>
                    </div>

                    <div className="member-actions-col">
                      <span className={`member-role-badge ${memberIsAdmin ? 'admin' : memberIsCoAdmin ? 'co-admin' : 'member'}`}>
                        {memberIsAdmin ? '👑 Admin' : memberIsCoAdmin ? '🥈 Co-Admin' : 'Member'}
                      </span>
                      {!isMe && !memberIsAdmin && isCreatorOrAdmin && (
                        <button
                          className="btn btn-secondary btn-sm"
                          style={{ marginRight: '8px' }}
                          onClick={() => handleToggleCoAdmin(member.userId, member.role)}
                          disabled={loading}
                        >
                          {memberIsCoAdmin ? 'Demote' : 'Make Co-Admin'}
                        </button>
                      )}
                      {!isMe && !memberIsAdmin && (isCreatorOrAdmin || (isCoAdmin && !memberIsCoAdmin)) && (
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleRemoveMember(member.userId, member.userName)}
                          disabled={loading}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeSubTab === 'summary' && (() => {
          const selectedMember = group.members.find(m => m.userId === selectedMemberId);
          
          let totalPaid = 0;
          let totalShare = 0;
          let settlementsSent = 0;
          let settlementsReceived = 0;
          const memberExpenses = [];

          expenses.forEach(exp => {
            const isSettlement = exp.description.toLowerCase().includes('settle');
            const paidByMe = exp.paidByUserId === selectedMemberId;
            const myParticipant = exp.participants?.find(p => p.userId === selectedMemberId);
            const isParticipant = !!myParticipant;

            if (isSettlement) {
              if (paidByMe) {
                settlementsSent += exp.amount;
                memberExpenses.push({
                  ...exp,
                  role: 'settlement-sent',
                  amountEffect: exp.amount
                });
              } else if (isParticipant) {
                settlementsReceived += myParticipant.amountOwed;
                memberExpenses.push({
                  ...exp,
                  role: 'settlement-received',
                  amountEffect: myParticipant.amountOwed
                });
              }
            } else {
              if (paidByMe || isParticipant) {
                if (paidByMe) {
                  totalPaid += exp.amount;
                }
                if (isParticipant) {
                  totalShare += myParticipant.amountOwed;
                }
                memberExpenses.push({
                  ...exp,
                  role: paidByMe && isParticipant ? 'payer-and-participant' : paidByMe ? 'payer-only' : 'participant-only',
                  myOwed: myParticipant ? myParticipant.amountOwed : 0
                });
              }
            }
          });

          // Sort member expenses descending by createdAt
          memberExpenses.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

          const netOutstanding = totalShare - totalPaid + settlementsReceived - settlementsSent;

          return (
            <div className="member-summary-container">
              {/* Left Sidebar: Members List */}
              <div className="member-summary-sidebar bg-glass">
                <h4>Group Members</h4>
                <div className="summary-member-list">
                  {group.members.map((m) => {
                    const isSelected = m.userId === selectedMemberId;
                    return (
                      <div
                        key={m.userId}
                        className={`summary-member-row ${isSelected ? 'active' : ''}`}
                        onClick={() => setSelectedMemberId(m.userId)}
                      >
                        <div className="member-avatar">
                          {m.userName.substring(0, 2).toUpperCase()}
                        </div>
                        <div className="member-info">
                          <span className="member-name">{m.userName}</span>
                          <span className="member-email">{m.userEmail}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right Pane: Member Details */}
              {selectedMember ? (
                <div className="member-summary-details">
                  <div className="member-details-header card-box">
                    <div className="member-large-avatar-circle">
                      {selectedMember.userName.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="member-header-info">
                      <h2>{selectedMember.userName}</h2>
                      <p>{selectedMember.userEmail}</p>
                      <span className={`member-role-badge ${selectedMember.role}`}>
                        {selectedMember.role === 'admin' ? '👑 Admin' : selectedMember.role === 'co-admin' ? '🥈 Co-Admin' : 'Member'}
                      </span>
                    </div>
                  </div>

                  {/* Metrics Grid */}
                  <div className="summary-metrics-grid">
                    <div className="metric-card bg-glass">
                      <span className="metric-label">Total Paid (Expenses)</span>
                      <span className="metric-value positive">₹{totalPaid.toFixed(2)}</span>
                    </div>
                    <div className="metric-card bg-glass">
                      <span className="metric-label">Total Share (Expenses)</span>
                      <span className="metric-value negative">₹{totalShare.toFixed(2)}</span>
                    </div>
                    <div className="metric-card bg-glass">
                      <span className="metric-label">Settlements Sent</span>
                      <span className="metric-value positive">₹{settlementsSent.toFixed(2)}</span>
                    </div>
                    <div className="metric-card bg-glass">
                      <span className="metric-label">Settlements Received</span>
                      <span className="metric-value negative">₹{settlementsReceived.toFixed(2)}</span>
                    </div>
                    <div className={`metric-card outstanding-card ${netOutstanding > 0.01 ? 'owe' : netOutstanding < -0.01 ? 'owed' : 'settled'}`}>
                      <span className="metric-label">Net Outstanding Balance</span>
                      <span className="metric-value">
                        {netOutstanding > 0.01 
                          ? `Owes ₹${netOutstanding.toFixed(2)}` 
                          : netOutstanding < -0.01 
                            ? `Is Owed ₹${Math.abs(netOutstanding).toFixed(2)}` 
                            : 'All Settled Up 🎉'
                        }
                      </span>
                      <span className="metric-sub">
                        {netOutstanding > 0.01 
                          ? 'needs to pay others' 
                          : netOutstanding < -0.01 
                            ? 'needs to be paid back' 
                            : 'no pending balances'
                        }
                      </span>
                    </div>
                  </div>

                  {/* Member's Expense Feed */}
                  <div className="member-expenses-feed card-box">
                    <h3>⏳ Expense History ({memberExpenses.length})</h3>
                    {memberExpenses.length === 0 ? (
                      <div className="empty-state">
                        <p>No expenses involving {selectedMember.userName} yet.</p>
                      </div>
                    ) : (
                      <div className="feed-list">
                        {memberExpenses.map((exp) => {
                          const isSettlement = exp.description.toLowerCase().includes('settle');
                          return (
                            <div key={exp.id} className={`feed-row ${isSettlement ? 'settlement-feed-row' : ''}`}>
                              <div className="feed-emoji">{getCategoryEmoji(exp.description)}</div>
                              <div className="feed-details">
                                <span className="feed-desc">{exp.description}</span>
                                <span className="feed-date">{formatDate(exp.createdAt)}</span>
                              </div>
                              <div className="feed-role-badge-col">
                                {exp.role === 'settlement-sent' && (
                                  <span className="badge role-settlement-sent">Sent Settlement</span>
                                )}
                                {exp.role === 'settlement-received' && (
                                  <span className="badge role-settlement-received">Received Settlement</span>
                                )}
                                {exp.role === 'payer-and-participant' && (
                                  <span className="badge role-payer-participant">Paid & Shared</span>
                                )}
                                {exp.role === 'payer-only' && (
                                  <span className="badge role-payer">Paid Only</span>
                                )}
                                {exp.role === 'participant-only' && (
                                  <span className="badge role-participant">Shared Only</span>
                                )}
                              </div>
                              <div className="feed-amounts">
                                <div className="feed-total-amount">Total: ₹{exp.amount.toFixed(2)}</div>
                                <div className="feed-impact-amount">
                                  {exp.role === 'settlement-sent' && `Sent: ₹${exp.amountEffect.toFixed(2)}`}
                                  {exp.role === 'settlement-received' && `Received: ₹${exp.amountEffect.toFixed(2)}`}
                                  {exp.role === 'payer-and-participant' && `Paid ₹${exp.amount.toFixed(2)} (Owed ₹${exp.myOwed.toFixed(2)})`}
                                  {exp.role === 'payer-only' && `Paid ₹${exp.amount.toFixed(2)} (Owed ₹0.00)`}
                                  {exp.role === 'participant-only' && `Owed ₹${exp.myOwed.toFixed(2)}`}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="empty-state-pane card-box">
                  <p>Please select a member to view details.</p>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {(eventModal?.type === 'create' || (eventModal?.type === 'edit' && modalEvent)) && (
        <CreateEventModal
          group={group}
          users={users}
          currentUser={currentUser}
          eventToEdit={eventModal.type === 'edit' ? modalEvent : null}
          onClose={() => setEventModal(null)}
          onSuccess={handleEventsChanged}
        />
      )}

      {eventModal?.type === 'item' && modalEvent && (
        <EventItemModal
          event={modalEvent}
          users={users}
          currentUser={currentUser}
          itemToEdit={eventModal.item}
          changeTotal={!!eventModal.changeTotal}
          onClose={() => setEventModal(null)}
          onSuccess={handleEventsChanged}
        />
      )}

      {eventModal?.type === 'payment' && modalEvent && (
        <RecordPaymentModal
          event={modalEvent}
          users={users}
          currentUser={currentUser}
          initialItemId={eventModal.itemId}
          onClose={() => setEventModal(null)}
          onSuccess={handleEventsChanged}
        />
      )}
    </div>
  );
}

export default GroupDetailView;
